import { useEffect, useState } from 'react';

/**
 * SSR-safe media-query hook.
 *
 * Returns `initialValue` on the server and on the first client render, then
 * updates once mounted and stays in sync with viewport changes. The default
 * `initialValue` of `false` matches a mobile-first base (i.e. the query is
 * assumed not to match until the client can evaluate it). Pass `initialValue:
 * true` when a component should optimistically assume the query matches on the
 * first paint — e.g. to avoid briefly rendering (and tracking) extra
 * desktop-only content before the query resolves.
 *
 * @param query - A CSS media query string, e.g. `(min-width: 768px)`.
 * @param initialValue - Value used on the server and first client render (default `false`).
 *
 * @example
 * ```tsx
 * const isDesktop = useMediaQuery('(min-width: 768px)');
 * ```
 */
export const useMediaQuery = (query: string, initialValue = false): boolean => {
	const [matches, setMatches] = useState(initialValue);

	useEffect(() => {
		const mediaQueryList = window.matchMedia(query);
		const update = (): void => setMatches(mediaQueryList.matches);

		update();
		mediaQueryList.addEventListener('change', update);

		return () => mediaQueryList.removeEventListener('change', update);
	}, [query]);

	return matches;
};
